/**
 * Delivery History
 * Shows today's deliveries in a list
 */

import { useState } from 'react'
import { Package, MapPin, Clock, Image, FileText, Trash2, X } from 'lucide-react'
import { Card, Badge, Button } from '../ui'
import { useAppStore } from '../../stores'
import { formatTime } from '../../utils/helpers'

// =============================================================================
// COMPONENT
// =============================================================================

export function DeliveryHistory() {
  const getTodayDeliveries = useAppStore((state) => state.getTodayDeliveries)
  const deleteDelivery = useAppStore((state) => state.deleteDelivery)
  const deliveries = getTodayDeliveries()
  const [expandedPhoto, setExpandedPhoto] = useState<string | null>(null)
  
  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this delivery? This action cannot be undone.')) {
      deleteDelivery(id)
      window.location.reload()
    }
  }
  
  // Device detection for map links
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent)
  }
  
  const getMapUrl = (lat: number, lng: number) => {
    if (isIOS()) {
      return `https://maps.apple.com/?q=${lat},${lng}`
    } else {
      return `https://www.google.com/maps?q=${lat},${lng}`
    }
  }
  
  if (deliveries.length === 0) {
    return (
      <Card>
        <div className="text-center py-8">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No deliveries logged today</p>
          <p className="text-sm text-gray-400 mt-1">
            Start logging deliveries using the form above
          </p>
        </div>
      </Card>
    )
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-medium text-gray-900">Today's Deliveries</h2>
        <Badge variant="secondary">{deliveries.length}</Badge>
      </div>
      
      <div className="space-y-3">
        {deliveries.map((delivery, index) => (
          <Card key={delivery.id} className="p-4">
            <div className="flex gap-4">
              {/* Sequence Number */}
              <div className="flex shrink-0 w-8 h-8 bg-gray-100 rounded-full items-center justify-center">
                <span className="text-sm font-medium text-gray-600">
                  {deliveries.length - index}
                </span>
              </div>
              
              {/* Thumbnail */}
              <div className="flex shrink-0">
                {delivery.photo ? (
                  <button
                    onClick={() => setExpandedPhoto(delivery.photo!)}
                    className="w-16 h-16 rounded-lg overflow-hidden hover:opacity-80 transition-opacity cursor-pointer"
                  >
                    <img
                      src={delivery.photo}
                      alt="Delivery"
                      className="w-full h-full object-cover"
                    />
                  </button>
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Image className="w-6 h-6 text-gray-400" />
                  </div>
                )}
              </div>
              
              {/* Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>{formatTime(new Date(delivery.timestamp))}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        delivery.status === 'synced'
                          ? 'success'
                          : delivery.status === 'delivered'
                          ? 'success'
                          : delivery.status === 'pending'
                          ? 'warning'
                          : 'danger'
                      }
                      size="sm"
                    >
                      {delivery.status === 'delivered' ? 'Delivered' : delivery.status}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(delivery.id)}
                      className="h-7 w-7 p-0 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Location */}
                <div className="flex items-center gap-1.5 mt-2 text-sm text-gray-500">
                  <MapPin className="w-4 h-4 flex shrink-0" />
                  <a 
                    href={getMapUrl(delivery.location.latitude, delivery.location.longitude)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Location
                  </a>
                </div>
                
                {/* Note */}
                {delivery.note && (
                  <div className="flex items-start gap-1.5 mt-2">
                    <FileText className="w-4 h-4 flex shrink-0 text-gray-400 mt-0.5" />
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {delivery.note}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Photo Modal */}
      {expandedPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
          onClick={() => setExpandedPhoto(null)}
        >
          <button
            onClick={() => setExpandedPhoto(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={expandedPhoto}
            alt="Expanded delivery photo"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  )
}
